import '../financials/financialsResponsive.css';
// BL-173 Gate 3c — staff manual card entry, on a client's behalf.
// Spec: IntelAgent_BL173_Payments_Handling_Collection_v1_00.md (§6 Gate 3, §10 Gate 3c).
//
// Same tokenization path as the self-service flow, running inside the staff CRM instead of the
// client portal: AddCardForm mounts Stripe's own Elements iframe and confirms the SetupIntent
// client-side, so the card number, expiry and CVC never reach IntelAgent's servers no matter who
// is typing. There is no second card-entry path here — this component only chooses the owner
// (this CRM account) and reuses Gates 1–3b's create_setup_intent / save_payment_method /
// list_payment_methods / remove_payment_method actions unchanged.
//
// The staff-only gate is the edge function's own org_members.role in (owner, admin) check on
// account-owned cards (Gate 1/2). The role read below is a display courtesy so the panel can say
// why it is hidden — it is not the control, and the function refuses the write regardless.
//
// §6 names the thing the technology does not solve: a staff member hearing a card number read
// aloud over the phone is an operational exposure that tokenization does not remove. The panel
// says so on screen rather than implying "Stripe handles it".

import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabaseClient } from '../../config/supabase';
import AddCardForm from './AddCardForm';
import { SupportWriteGuard } from '../../context/SupportSessionContext';
import { ACCOUNT_CARD_CAP, describeCard, isCardExpired, sortCards } from '../../lib/stripeCards';

const EDGE_FUNCTION = 'stripe-setup-intent';

// Mirrors PRIVILEGED_ROLES in supabase/functions/stripe-setup-intent/index.ts — the same two
// roles, read here only to decide what to render.
const PRIVILEGED_ROLES = ['owner', 'admin'];

function formatAddedOn(value) {
    if (!value) return '';
    const when = new Date(value);
    return Number.isNaN(when.getTime()) ? '' : when.toLocaleDateString();
}

export default function StaffCardEntryPanel({
    account,
    publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY,
    client = supabaseClient,
    stripeLoader = undefined
}) {
    const accountId = account?.id ?? null;
    const accountName = account?.account_name || 'this client';

    const [role, setRole] = useState(null);
    const [roleLoading, setRoleLoading] = useState(true);
    const [cards, setCards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyCardId, setBusyCardId] = useState(null);
    const [confirmingRemovalId, setConfirmingRemovalId] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');

    const isPrivileged = PRIVILEGED_ROLES.includes(role);

    const owner = useMemo(
        () => ({ ownerType: 'account', portalAccountId: null, accountId, label: 'company' }),
        [accountId]
    );

    const invoke = useCallback(
        async (payload) => {
            const { data, error: invokeError } = await client.functions.invoke(EDGE_FUNCTION, { body: payload });
            if (invokeError) {
                throw new Error(invokeError.message || 'The card service could not be reached.');
            }
            if (data?.error) {
                throw new Error(data.error);
            }
            return data;
        },
        [client]
    );

    useEffect(() => {
        let cancelled = false;

        async function loadRole() {
            setRoleLoading(true);
            try {
                const { data: authData } = await client.auth.getUser();
                const userId = authData?.user?.id;
                if (!userId) {
                    if (!cancelled) setRole(null);
                    return;
                }
                const { data: membership } = await client
                    .from('org_members')
                    .select('role')
                    .eq('user_id', userId)
                    .maybeSingle();
                if (!cancelled) setRole(membership?.role ?? null);
            } catch {
                if (!cancelled) setRole(null);
            } finally {
                if (!cancelled) setRoleLoading(false);
            }
        }

        loadRole();
        return () => {
            cancelled = true;
        };
    }, [client]);

    const loadCards = useCallback(async () => {
        if (!accountId) {
            setCards([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        setError('');
        try {
            const data = await invoke({ action: 'list_payment_methods', ...owner });
            setCards(sortCards(data?.paymentMethods ?? []));
        } catch (loadError) {
            setCards([]);
            setError(loadError.message || 'The cards on file could not be loaded.');
        } finally {
            setLoading(false);
        }
    }, [accountId, invoke, owner]);

    useEffect(() => {
        if (roleLoading || !isPrivileged) {
            setLoading(false);
            return;
        }
        loadCards();
    }, [isPrivileged, loadCards, roleLoading]);

    const removeCard = useCallback(
        async (card) => {
            setBusyCardId(card.id);
            setError('');
            setNotice('');
            try {
                const data = await invoke({ action: 'remove_payment_method', ...owner, paymentMethodRowId: card.id });
                setCards(sortCards(data?.paymentMethods ?? []));
                setNotice('Card removed.');
            } catch (actionError) {
                setError(actionError.message || 'That card could not be removed.');
            } finally {
                setBusyCardId(null);
                setConfirmingRemovalId(null);
            }
        },
        [invoke, owner]
    );

    const handleSaved = useCallback(() => {
        setShowAddForm(false);
        setNotice('Card added and attributed to you.');
        loadCards();
    }, [loadCards]);

    if (roleLoading) {
        return <div className="text-sm text-[var(--ink-muted)]">Checking your access…</div>;
    }

    if (!isPrivileged) {
        return (
            <div className="text-sm text-[var(--ink-muted)]">
                Only an owner or admin can add a card on a client&apos;s behalf.
            </div>
        );
    }

    if (!accountId) {
        return (
            <div className="text-sm text-[var(--ink-muted)]">
                This client has no account record yet, so there is nothing to attach a card to.
            </div>
        );
    }

    const atCap = cards.length >= ACCOUNT_CARD_CAP;

    return (
        <section className="ia-financials space-y-3" aria-label="Card on file">
            <div>
                <div className="text-sm font-medium text-[var(--ink)]">Card on file</div>
                <p className="mt-1 text-xs text-[var(--ink-muted)] leading-relaxed">
                    {`One shared card can be kept on file for ${accountName}. Add it here when the client cannot do it themselves — a phone booking, or in person.`}
                </p>
                <p className="mt-1 text-xs text-[var(--ink-muted)] leading-relaxed">
                    The number is typed straight into Stripe&apos;s own field and never reaches our servers.
                    Taking a card number aloud over the phone is still an exposure that no software removes —
                    follow the call-handling procedure.
                </p>
            </div>

            {error && (
                <p role="alert" className="text-sm text-[var(--flow-orange)]">
                    {error}
                </p>
            )}

            {notice && (
                <p role="status" className="text-sm text-[var(--flow-green)]">
                    {notice}
                </p>
            )}

            {loading ? (
                <p className="text-sm text-[var(--ink-muted)]">Loading the card on file…</p>
            ) : cards.length === 0 ? (
                <p className="text-sm text-[var(--ink-muted)]">No card on file for this client.</p>
            ) : (
                <ul className="space-y-2">
                    {cards.map((card) => {
                        const busy = busyCardId === card.id;
                        const addedOn = formatAddedOn(card.created_at);
                        return (
                            <li
                                key={card.id}
                                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-white/15 bg-[var(--surface-sunken)] p-3"
                            >
                                <div>
                                    <p className="text-sm font-medium text-[var(--ink)]">{describeCard(card)}</p>
                                    <p className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-[var(--ink-muted)]">
                                        {/* added_by carries the staff user id (Gate 3a). It is the only
                                            audit signal on the row today — see
                                            Docs/Open_Questions_BL173_Gate3c_Build.md item 1. */}
                                        <span>
                                            {card.added_by
                                                ? `Staff-entered${addedOn ? ` on ${addedOn}` : ''}`
                                                : `Client-entered${addedOn ? ` on ${addedOn}` : ''}`}
                                        </span>
                                        {isCardExpired(card) && (
                                            <span className="rounded bg-red-500/20 px-1.5 py-0.5 text-red-200">Expired</span>
                                        )}
                                    </p>
                                </div>

                                <div className="flex items-center gap-2">
                                    {confirmingRemovalId === card.id ? (
                                        <>
                                            <button
                                                type="button"
                                                disabled={busy}
                                                onClick={() => removeCard(card)}
                                                className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
                                            >
                                                {busy ? 'Removing…' : 'Confirm remove'}
                                            </button>
                                            <button
                                                type="button"
                                                disabled={busy}
                                                onClick={() => setConfirmingRemovalId(null)}
                                                className="rounded-md px-2 py-1.5 text-xs text-[var(--ink-muted)] disabled:opacity-50"
                                            >
                                                Cancel
                                            </button>
                                        </>
                                    ) : (
                                        <SupportWriteGuard>
                                            <button
                                                type="button"
                                                disabled={busy}
                                                onClick={() => setConfirmingRemovalId(card.id)}
                                                className="rounded-md border border-white/20 px-3 py-1.5 text-xs text-gray-300 hover:bg-white/5 disabled:opacity-50"
                                            >
                                                Remove
                                            </button>
                                        </SupportWriteGuard>
                                    )}
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}

            {/* The 1-card-per-account cap is enforced by the Gate 3a trigger; hiding the button is
                courtesy, not the control. */}
            {!loading && atCap && (
                <p className="text-xs text-[var(--ink-muted)]">
                    This client already has a card on file. Remove it to add a different one.
                </p>
            )}

            {!loading && !atCap && !showAddForm && (
                <SupportWriteGuard>
                    <button
                        type="button"
                        onClick={() => setShowAddForm(true)}
                        className="rounded-lg bg-[var(--btn-fill)] px-4 py-2 text-sm font-medium text-[var(--btn-ink)] hover:bg-[var(--btn-fill-hover)]"
                    >
                        Add a card for this client
                    </button>
                </SupportWriteGuard>
            )}

            {showAddForm && (
                <div className="space-y-2">
                    <AddCardForm
                        ownerType="account"
                        accountId={accountId}
                        publishableKey={publishableKey}
                        client={client}
                        {...(stripeLoader ? { stripeLoader } : {})}
                        onSaved={handleSaved}
                    />
                    <button
                        type="button"
                        onClick={() => setShowAddForm(false)}
                        className="text-xs text-[var(--ink-muted)] underline"
                    >
                        Cancel
                    </button>
                </div>
            )}
        </section>
    );
}
